import { useEffect, useState } from 'react';

import {
  TertiaryButton,
  Typography,
} from '@changegps/horizon-component-library';
import { Box, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';

import {
  useCIMDisconnect,
  useGetFuseSignConnect,
  useGetFuseSignDisconnect,
  useGetXPMAuthURL,
  useGetXPMConnect,
} from '@/features/clients';
import { ConnectDisconnectGoCardlessButton } from '@/features/gocardless';
import { useUserQuery } from '@/features/users';
import { XeroInvoiceToggleButton } from '@/features/xero';

import { AccessConnect } from '../components/AccessConnect';
import { XpmApiGuide } from '../components/XpmApiGuide';

export const SettingsIntegrations = () => {
  const { refetch: refetchUserQuery, ...userQuery } = useUserQuery();
  const [isXeroConnected, setIsXeroConnected] = useState(false);
  const [openXPMGuide, setOpenXPMGuide] = useState(false);
  const [isFusesignConnected, setIsFusesignConnected] = useState(false);

  const [queryData, setQueryData] = useState<{
    code?: string | null;
    company_id?: string | null;
  }>({
    code: null,
    company_id: null,
  });

  const navigate = useLocation();

  const { data: url, refetch } = useGetXPMAuthURL();
  const { data: xpmData } = useGetXPMConnect(queryData);
  const { data: fuseSignData, refetch: _getRedirectUrl } =
    useGetFuseSignConnect();

  const { refetch: disconnectFuseSign } = useGetFuseSignDisconnect();

  const { refetch: disconnect } = useCIMDisconnect(
    userQuery.data?.connection_type === 'ADE' ||
      userQuery.data?.connection_type === 'XPM'
      ? userQuery.data.connection_type
      : 'ADE',
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(navigate.search);
    const code = queryParams.get('code');
    const state = queryParams.get('state');
    const company_id = queryParams.get('company_id');

    if (code && state === 'xpm') {
      setQueryData({
        code,
        company_id,
      });

      window.history.replaceState(null, '', '/settings/integrations');
    }
  }, [navigate]);

  useEffect(() => {
    if (url) {
      // Add state parameter to identify XPM integration
      const urlWithState = new URL(url.authUrl);
      urlWithState.searchParams.set('state', 'xpm');
      window.location.href = urlWithState.toString();
    }
  }, [url]);

  useEffect(() => {
    if (userQuery.isSuccess) {
      setIsXeroConnected(
        String(userQuery.data.connection_type).toUpperCase() === 'XPM',
      );
      setIsFusesignConnected(userQuery.data.is_fusesign_connected);
    }
  }, [userQuery]);

  useEffect(() => {
    if (xpmData) {
      void refetchUserQuery();
    }
  }, [refetchUserQuery, xpmData]);

  const handleClick = async () => {
    if (!isXeroConnected) {
      void refetch();
    } else {
      await disconnect();
      await refetchUserQuery();
    }
  };

  const handleFuseSignClick = async () => {
    if (!userQuery.data?.is_fusesign_connected) {
      if (fuseSignData?.redirect_url) {
        window.open(fuseSignData.redirect_url, '_self');
      }
    } else {
      await disconnectFuseSign();
      await refetchUserQuery();
    }
  };

  return (
    <Box
      sx={{
        padding: '1em',
      }}
    >
      <AccessConnect />
      <Box
        sx={{
          marginY: '1em',
        }}
      >
        <Divider />
      </Box>
      <Box sx={{ display: 'flex', gap: '50px' }}>
        <Box
          sx={{
            marginTop: '1em',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            Xero Practice Manager API
          </Typography>
          <Box
            sx={{
              marginTop: '.5em',
            }}
          >
            <TertiaryButton
              onClick={handleClick}
              sx={{
                width: '150px',
              }}
            >
              {!isXeroConnected ? 'Enable' : 'Disable'}
            </TertiaryButton>
          </Box>
        </Box>
        <Box
          sx={{
            marginTop: '1em',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            Xero Invoicing
          </Typography>
          <Box
            sx={{
              marginTop: '.5em',
            }}
          >
            <XeroInvoiceToggleButton
              sx={{
                width: '150px',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: '1em',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            GoCardless
          </Typography>
          <Box
            sx={{
              marginTop: '.5em',
            }}
          >
            <ConnectDisconnectGoCardlessButton
              sx={{
                width: '150px',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: '1em',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            FuseSign
          </Typography>
          <Box
            sx={{
              marginTop: '.5em',
            }}
          >
            <TertiaryButton
              onClick={handleFuseSignClick}
              sx={{
                width: '150px',
              }}
            >
              {!isFusesignConnected ? 'Enable' : 'Disable'}
            </TertiaryButton>
          </Box>
        </Box>
        <Box
          sx={{
            marginTop: '1em',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            How do I setup my XPM API?
          </Typography>
          <Box
            sx={{
              marginTop: '.5em',
            }}
          >
            <TertiaryButton
              sx={{
                width: '150px',
              }}
              onClick={() => setOpenXPMGuide(true)}
            >
              Show Guide
            </TertiaryButton>
          </Box>
        </Box>
      </Box>
      <XpmApiGuide open={openXPMGuide} onClose={() => setOpenXPMGuide(false)} />
    </Box>
  );
};
